import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { blogApi } from "../../api/blogApi";
import { useAuth } from "../../context/AuthContext";
import "./BlogListPage.css"; // CSS 파일 임포트

/* ─────────────────────────────────────────────
   디자인 토큰 (기존 부분적 사용을 위해 유지)
───────────────────────────────────────────── */
const T = {
  bg:        "var(--color-background)",
  bgSecond:  "var(--color-background-secondary)",
  border:    "var(--color-border-secondary)",
  text:      "var(--color-text-primary)",
  textMuted: "var(--color-text-secondary)",
  accent:    "#378ADD",
  radius:    "8px",
};

/* ─────────────────────────────────────────────
   SearchBar
───────────────────────────────────────────── */
const SearchBar = ({ value, onChange, onSubmit }) => {
  return (
    <form
      className="blog-search-form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(value); }}
    >
      <input
        type="text"
        className="blog-search-input"
        placeholder="제목 또는 내용 검색…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button type="submit" className="blog-search-btn">
        검색
      </button>
    </form>
  );
};

/* ─────────────────────────────────────────────
   TagNav (Velog-style top horizontal scroll)
───────────────────────────────────────────── */
const TagNav = ({ tags, activeTags, onSelect }) => {
  return (
    <nav className="blog-tag-nav">
      <button
        onClick={() => onSelect("")}
        className={`blog-tag-btn ${activeTags.length === 0 ? 'active' : ''}`}
      >
        <span>전체</span>
      </button>

      {tags.map((tag) => {
        const isActive = activeTags.includes(tag.name);
        return (
          <button
            key={tag.id}
            onClick={() => onSelect(tag.name)}
            className={`blog-tag-btn ${isActive ? 'active' : ''}`}
          >
            <span>{tag.name}</span>
            <span className="blog-tag-count">{tag.post_count}</span>
          </button>
        );
      })}
    </nav>
  );
};

/* ─────────────────────────────────────────────
   PostCard (Velog-style Grid Card)
───────────────────────────────────────────── */
const PostCard = ({ post, onClick }) => (
  <article className="post-card" onClick={onClick}>
    <div className="post-card-thumb-wrapper">
      {post.thumbnail_url ? (
        <img
          src={post.thumbnail_url}
          alt={post.title}
          className="post-card-thumb"
        />
      ) : (
        <div className="post-card-thumb-empty">
          No Image
        </div>
      )}
    </div>

    <div className="post-card-content">
      <h2 className="post-card-title">{post.title}</h2>
      
      {/* 백엔드에서 생성해준 excerpt (본문 요약) */}
      <p className="post-card-excerpt">
        {post.excerpt || ""}
      </p>

      {post.tags?.length > 0 && (
        <div className="post-card-tags">
          {post.tags.map((tag) => (
            <span key={tag} className="post-card-tag">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>

    <div className="post-card-footer">
      <span>{new Date(post.created_at).toLocaleDateString("ko-KR")}</span>
      <span>조회 {post.views?.toLocaleString()}</span>
    </div>
  </article>
);

/* ─────────────────────────────────────────────
   Pagination
───────────────────────────────────────────── */
const Pagination = ({ page, total, limit, onPage }) => {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  return (
    <div className="blog-pagination">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`blog-page-btn ${p === page ? 'active' : ''}`}
        >
          {p}
        </button>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   BlogListPage
───────────────────────────────────────────── */
const BlogListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  const [posts, setPosts]         = useState([]);
  const [tags, setTags]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  const search  = searchParams.get("search") || "";
  const tagParam = searchParams.get("tag") || "";
  const activeTags = tagParam ? tagParam.split(",") : [];
  
  const page    = Number(searchParams.get("page") || 1);
  const LIMIT   = 12; // 벨로그 스타일은 3x4나 4x3 형태로 보여주기 좋으므로 12로 변경

  // 태그 목록 로드
  useEffect(() => {
    blogApi.getTags().then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  // 게시글 목록 로드
  useEffect(() => {
    setLoading(true);
    blogApi.getList({ search, tag: tagParam, page, limit: LIMIT })
      .then(({ data }) => {
        setPosts(data.posts);
        setTotal(data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, tagParam, page]);

  const updateParams = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    next.delete("page"); // 필터 변경 시 1페이지로
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handleSearch = (q) => {
    setSearchInput(q);
    updateParams({ search: q });
  };

  const handleTagToggle = (t) => {
    if (!t) {
      updateParams({ tag: "" });
      return;
    }
    
    let nextTags = [...activeTags];
    if (nextTags.includes(t)) {
      nextTags = nextTags.filter(x => x !== t);
    } else {
      nextTags.push(t);
    }
    updateParams({ tag: nextTags.join(",") });
  };

  const handlePage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", p);
    setSearchParams(next);
  };

  return (
    <div className="blog-list-container">
      {/* 상단 헤더 (타이틀 & 검색 & 새 글 작성) */}
      <div className="blog-list-header">
        <h1>Blog</h1>
        <div className="blog-header-right">
          <SearchBar value={searchInput} onChange={setSearchInput} onSubmit={handleSearch} />
          {isAdmin ? (
            <button className="blog-new-btn" onClick={() => navigate("/blog/write")}>
              새 글 작성
            </button>
          ) : (
            <button className="blog-new-btn" onClick={() => navigate("/blog/login")}>
              관리자 로그인
            </button>
          )}
        </div>
      </div>

      {/* 컨트롤 영역 (태그 네비게이션) */}
      <div className="blog-controls-wrapper">
        {tags.length > 0 && (
          <TagNav tags={tags} activeTags={activeTags} onSelect={handleTagToggle} />
        )}
      </div>

      {/* 메인 게시글 영역 */}
      <div>
        {/* 활성 필터 표시 (검색어가 있을 경우나, 다중 태그 선택시 직관성을 위해 유지) */}
        {(search || activeTags.length > 0) && (
          <div className="active-filter-chips">
            {search && (
              <span className="active-filter-chip">
                "{search}" 검색 결과
                <button onClick={() => { setSearchInput(""); handleSearch(""); }}>×</button>
              </span>
            )}
            {activeTags.map(tag => (
              <span key={tag} className="active-filter-chip">
                {tag}
                <button onClick={() => handleTagToggle(tag)}>×</button>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ color: T.textMuted, textAlign: "center", padding: "80px 0", fontSize: "15px" }}>
            포스트를 불러오는 중입니다...
          </div>
        ) : posts.length === 0 ? (
          <div style={{ color: T.textMuted, textAlign: "center", padding: "80px 0", fontSize: "15px" }}>
            조건에 맞는 게시글이 없습니다.
          </div>
        ) : (
          <>
            <div className="blog-post-grid">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onClick={() => navigate(`/blog/${post.id}`)}
                />
              ))}
            </div>
            <Pagination page={page} total={total} limit={LIMIT} onPage={handlePage} />
          </>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
